import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

const DEMO_DATA = {
  contacts: [
    { id: 1, category: 'attorney', icon: '⚖️', name: 'Jane A. Smith', company: 'Smith & Associates Law', phone: '(555) 111-0001', email: 'jsmith@example-law.com' },
    { id: 2, category: 'accountant', icon: '🧾', name: 'Robert Johnson', company: 'Johnson Accounting Group', phone: '(555) 111-0002', email: 'rjohnson@example-cpa.com' },
    { id: 3, category: 'doctor', icon: '🩺', name: 'Dr. Maria Garcia', company: 'Example Family Medicine', phone: '(555) 111-0003', email: 'mgarcia@example-medical.com' },
  ],
  accounts: [
    { id: 1, type: 'checking', icon: '🏦', name: 'Example Bank — Joint Checking', last4: '0001', value: '$12,000' },
    { id: 2, type: 'investment', icon: '📈', name: 'Example Investments — Brokerage', last4: '0002', value: '$250,000' },
    { id: 3, type: 'retirement', icon: '🏖️', name: 'Example Fund — 401(k)', last4: '0003', value: '$500,000' },
  ],
  assets: [
    { id: 1, type: 'real_estate', icon: '🏠', name: '123 Main Street, Anytown USA', value: '$450,000', location: 'Deed in safe deposit box at Example Bank' },
    { id: 2, type: 'vehicle', icon: '🚗', name: '2022 Example Make & Model', value: '$28,000', location: 'Title stored in home filing cabinet' },
  ],
  insurance: [
    { id: 1, type: 'life', icon: '❤️', name: 'Example Life Insurance Co.', policy: 'EX-0001-00', coverage: '$500,000', beneficiary: 'Spouse 100%' },
    { id: 2, type: 'home', icon: '🏠', name: 'Example Home Insurance Co.', policy: 'EX-0002-00', coverage: '$450,000' },
  ],
  documents: [
    { id: 1, type: 'will', icon: '📜', name: 'Last Will & Testament', date: 'Updated January 2024' },
    { id: 2, type: 'trust', icon: '🏛️', name: 'Revocable Living Trust', date: 'Created 2020' },
    { id: 3, type: 'power_of_attorney', icon: '✍️', name: 'Durable Power of Attorney', date: 'Updated January 2024' },
  ],
  digital: [
    { id: 1, type: 'email', icon: '📧', name: 'Primary Email Account', username: 'example@email.com', action: 'Transfer to spouse' },
    { id: 2, type: 'financial', icon: '💳', name: 'Online Payment Account', username: 'example@email.com', action: 'Close account' },
  ],
  wishes: {
    funeral: 'Cremation',
    organ_donor: true,
    service: 'Small celebration of life, family only.',
    letters: ['To my spouse', 'To my children'],
  },
  guardians: [
    { name: 'Example Guardian One', relationship: 'Spouse', status: 'accepted' },
    { name: 'Example Guardian Two', relationship: 'Attorney', status: 'accepted' },
    { name: 'Example Guardian Three', relationship: 'Adult Child', status: 'invited' },
  ],
};

const SECTIONS = [
  { key: 'contacts',  icon: '👥', label: 'Key Contacts',    count: 3 },
  { key: 'accounts',  icon: '🏦', label: 'Accounts',        count: 3 },
  { key: 'assets',    icon: '🏡', label: 'Assets',          count: 2 },
  { key: 'insurance', icon: '🛡️', label: 'Insurance',       count: 2 },
  { key: 'documents', icon: '📄', label: 'Documents',        count: 3 },
  { key: 'digital',   icon: '💻', label: 'Digital Life',    count: 2 },
  { key: 'wishes',    icon: '💌', label: 'Final Wishes',    count: 1 },
  { key: 'guardians', icon: '🔐', label: 'Vault Guardians', count: 3 },
];

const SAMPLE_LETTER = {
  recipient: 'To My Daughter',
  preview: 'By the time you read this, I will be gone. But I want you to know...',
  full: `By the time you read this, I will be gone. But I want you to know that watching you grow into the person you are has been the greatest privilege of my life.

There are things I never quite found the words for when we were together, things I meant to say but life always seemed to get in the way. So I'm writing them down here, where you'll find them when you need them most.

I am so proud of you. Not for your achievements, though those have been many. I am proud of who you are, the kindness you show to strangers, the way you laugh, the way you love your own family now.

Take care of yourself first. That's not selfish, it's what I would want. And know that wherever I am, I am at peace, because I leave this world knowing you are in it.

All my love, always.`
};

export default function DemoVault() {
  const [vaultOpen, setVaultOpen] = useState(false);
  const [unlocking, setUnlocking] = useState(false);
  const [activeSection, setActiveSection] = useState(null);
  const [showPaywall, setShowPaywall] = useState(false);
  const [showLetter, setShowLetter] = useState(false);
  const navigate = useNavigate();

  const handleUnlock = () => {
    setUnlocking(true);
    setTimeout(() => { setVaultOpen(true); setUnlocking(false); }, 700);
  };

  const handleSectionClick = (key) => {
    setActiveSection(activeSection === key ? null : key);
  };

  const handleAddClick = () => {
    setShowPaywall(true);
  };

  const style = `
    /* DEMO VAULT SECTION */
    .demo-section {
      background: #1C2333;
      padding: 48px 48px 80px;
    }
    .demo-label {
      font-size: 11px; font-weight: 700; letter-spacing: 0.14em;
      text-transform: uppercase; color: var(--purple-l); margin-bottom: 16px;
      text-align: center;
    }
    .demo-title {
      font-family: 'Fraunces', serif; font-weight: 900;
      font-size: clamp(32px, 4vw, 52px); color: #fff;
      text-align: center; margin-bottom: 12px; line-height: 1.1;
    }
    .demo-title em { font-style: italic; color: var(--purple-l); }
    .demo-sub {
      text-align: center; color: rgba(255,255,255,0.7);
      font-size: 16px; margin-bottom: 48px; max-width: 500px; margin-left: auto; margin-right: auto;
      line-height: 1.6;
    }
    .demo-badge {
      display: inline-flex; align-items: center; gap: 8px;
      background: rgba(251,146,60,0.12); border: 1px solid rgba(251,146,60,0.25);
      border-radius: 100px; padding: 6px 16px; margin-bottom: 48px;
      font-size: 12px; font-weight: 600; color: #FB923C;
      display: flex; justify-content: center; width: fit-content; margin: 0 auto 48px;
    }

    /* DEMO VAULT CONTAINER */
    .demo-vault-wrap {
      max-width: 900px; margin: 0 auto;
      position: relative;
    }

    /* VAULT EXTERIOR */
    .demo-vault {
      width: 100%;
      background: linear-gradient(160deg, #3E434C 0%, #2A2E35 25%, #1E2128 55%, #2A2E35 80%, #3E434C 100%);
      border-radius: 20px; position: relative;
      box-shadow: 0 60px 120px rgba(0,0,0,0.55), 0 0 0 1px rgba(255,255,255,0.12),
        inset 0 2px 0 rgba(255,255,255,0.15), inset 0 -4px 0 rgba(0,0,0,0.3);
      overflow: visible;
    }
    .demo-wall-left {
      position: absolute; top: 10px; bottom: -10px; left: -20px; width: 26px;
      background: linear-gradient(90deg, #1A1D22, #32373F); border-radius: 6px 0 0 6px;
      box-shadow: -4px 8px 20px rgba(0,0,0,0.6); z-index: -1;
    }
    .demo-wall-right {
      position: absolute; top: 10px; bottom: -10px; right: -20px; width: 26px;
      background: linear-gradient(90deg, #32373F, #1A1D22); border-radius: 0 6px 6px 0;
      box-shadow: 4px 8px 20px rgba(0,0,0,0.6); z-index: -1;
    }
    .demo-wall-bottom {
      position: absolute; bottom: -18px; left: 10px; right: 10px; height: 22px;
      background: linear-gradient(180deg, #32373F, #1A1D22); border-radius: 0 0 10px 10px;
      box-shadow: 0 10px 24px rgba(0,0,0,0.6); z-index: -1;
    }

    /* RIVETS */
    .demo-rivets {
      display: flex; justify-content: space-between; align-items: center;
      padding: 8px 24px;
      background: linear-gradient(90deg, #32373F, #3E4349, #32373F);
      border-bottom: 2px solid rgba(0,0,0,0.3); position: relative; z-index: 1;
    }
    .demo-rivets.bottom { border-bottom: none; border-top: 2px solid rgba(0,0,0,0.3); border-radius: 0 0 20px 20px; }
    .demo-rivet {
      width: 10px; height: 10px; border-radius: 50%; flex-shrink: 0;
      background: radial-gradient(circle at 35% 35%, #8E9EAB, #32373F);
      box-shadow: inset 0 1px 3px rgba(0,0,0,0.5), 0 1px 0 rgba(255,255,255,0.15);
    }

    /* INNER FRAME */
    .demo-inner-frame {
      margin: 0 20px 14px; border: 5px solid #1A1D22; border-radius: 12px;
      overflow: hidden; position: relative; z-index: 1;
      box-shadow: inset 0 6px 24px rgba(0,0,0,0.7); background: #0D1117;
    }

    /* CLOSED DOOR */
    .demo-door-closed {
      position: relative; width: 100%; padding-bottom: 42%;
      background: linear-gradient(160deg, #4A4F58 0%, #32373F 25%, #252930 55%, #32373F 80%, #4A4F58 100%);
      overflow: hidden;
    }
    .demo-door-inner {
      position: absolute; inset: 0; display: flex; align-items: center;
      justify-content: space-between; padding: 0 60px 0 48px; gap: 32px;
    }
    .demo-door-inset { position: absolute; inset: 14px; border: 2px solid rgba(255,255,255,0.07); border-radius: 8px; box-shadow: inset 0 2px 10px rgba(0,0,0,0.4); }
    .demo-hinges { position: absolute; left: 16px; top: 0; bottom: 0; display: flex; flex-direction: column; justify-content: space-evenly; padding: 24px 0; z-index: 2; }
    .demo-hinge { width: 16px; height: 40px; background: linear-gradient(90deg, #6B7A86, #8E9EAB, #6B7A86); border-radius: 5px; box-shadow: inset 0 1px 0 rgba(255,255,255,0.3), 0 3px 6px rgba(0,0,0,0.5); }
    .demo-engraving { flex: 1; position: relative; z-index: 2; display: flex; flex-direction: column; gap: 6px; }
    .demo-engrave-eyebrow { font-size: 10px; font-weight: 700; letter-spacing: 0.18em; text-transform: uppercase; color: rgba(255,255,255,0.55); font-family: 'Plus Jakarta Sans', sans-serif; }
    .demo-engrave-name { font-family: 'Fraunces', serif; font-weight: 900; font-size: clamp(22px, 3vw, 40px); line-height: 1.05; color: rgba(255,255,255,0.88); text-shadow: 0 2px 6px rgba(0,0,0,0.5); }
    .demo-engrave-name em { font-style: italic; color: #C4B5FD; text-shadow: 0 2px 12px rgba(139,92,246,0.5); }
    .demo-engrave-sub { font-size: 13px; color: rgba(255,255,255,0.45); font-family: 'Plus Jakarta Sans', sans-serif; margin-top: 4px; line-height: 1.6; }
    .demo-dial-wrap { display: flex; flex-direction: column; align-items: center; gap: 12px; position: relative; z-index: 2; flex-shrink: 0; }
    .demo-dial { width: clamp(80px, 10vw, 130px); height: clamp(80px, 10vw, 130px); border-radius: 50%; background: radial-gradient(circle at 35% 30%, #5A6370, #32373F 50%, #1A1D22 100%); border: 4px solid rgba(255,255,255,0.1); box-shadow: 0 12px 32px rgba(0,0,0,0.6), inset 0 2px 0 rgba(255,255,255,0.15); display: flex; align-items: center; justify-content: center; position: relative; animation: demoDial 10s linear infinite; }
    .demo-dial.fast { animation: demoDial 0.3s linear infinite; }
    @keyframes demoDial { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
    .demo-dial-mark { position: absolute; top: 8px; left: 50%; transform: translateX(-50%); width: 4px; height: 16px; background: rgba(255,255,255,0.5); border-radius: 2px; }
    .demo-dial-inner { width: 45%; height: 45%; border-radius: 50%; background: radial-gradient(circle at 35% 35%, #4A4F58, #1A1D22); border: 2px solid rgba(255,255,255,0.06); }
    .demo-dial-label { font-size: 10px; font-weight: 700; letter-spacing: 0.16em; color: rgba(255,255,255,0.3); text-transform: uppercase; font-family: 'Plus Jakarta Sans', sans-serif; }
    .demo-unlock-btn { position: relative; z-index: 2; display: flex; align-items: center; justify-content: center; gap: 10px; width: 100%; padding: 16px; background: linear-gradient(135deg, #8B5CF6, #38BDF8); color: #fff; border: none; font-family: 'Plus Jakarta Sans', sans-serif; font-weight: 700; font-size: 16px; cursor: pointer; letter-spacing: 0.04em; transition: opacity 0.2s; box-shadow: 0 4px 20px rgba(139,92,246,0.4); }
    .demo-unlock-btn:hover { opacity: 0.9; }

    /* OPEN VAULT */
    .demo-open-wrap { position: relative; perspective: 1000px; }
    .demo-door-panel { position: absolute; top: 0; left: 0; bottom: 0; width: 52%; transform-origin: left center; transform-style: preserve-3d; transition: transform 1.2s cubic-bezier(0.4,0,0.2,1); z-index: 10; }
    .demo-door-panel.swung { transform: perspective(1000px) rotateY(-115deg); }
    .demo-door-panel-face { position: absolute; inset: 0; background: linear-gradient(160deg, #4A4F58 0%, #32373F 40%, #252930 100%); border-right: 5px solid rgba(0,0,0,0.5); }
    .demo-door-panel-face::before { content: ''; position: absolute; inset: 20px; border: 2px solid rgba(255,255,255,0.06); border-radius: 6px; }
    .demo-panel-hinges { position: absolute; left: 14px; top: 0; bottom: 0; display: flex; flex-direction: column; justify-content: space-evenly; padding: 40px 0; }
    .demo-panel-hinge { width: 18px; height: 52px; background: linear-gradient(90deg, #6B7A86, #9DAAB5, #6B7A86); border-radius: 5px; box-shadow: inset 0 2px 0 rgba(255,255,255,0.3); }

    /* INTERIOR */
    .demo-interior { background: linear-gradient(180deg, #1C2333 0%, #161D2B 100%); }
    .demo-int-bar { background: linear-gradient(90deg, #0D1117, #1a1f2e, #0D1117); padding: 12px 24px; display: flex; align-items: center; justify-content: space-between; border-bottom: 2px solid rgba(0,0,0,0.5); }
    .demo-int-lights { display: flex; align-items: center; gap: 10px; }
    .demo-light { width: 8px; height: 8px; border-radius: 50%; animation: demoLight 2.5s ease-in-out infinite; }
    .demo-light.g { background: #22C55E; box-shadow: 0 0 6px #22C55E; }
    .demo-light.a { background: #F59E0B; box-shadow: 0 0 6px #F59E0B; animation-delay: 0.7s; }
    @keyframes demoLight { 0%,100%{opacity:1} 50%{opacity:0.3} }
    .demo-int-title { font-size: 10px; font-weight: 700; letter-spacing: 0.12em; color: rgba(255,255,255,0.4); text-transform: uppercase; }
    .demo-int-badge { display: flex; align-items: center; gap: 5px; background: rgba(251,146,60,0.12); border: 1px solid rgba(251,146,60,0.25); border-radius: 100px; padding: 3px 10px; font-size: 10px; font-weight: 700; color: #FB923C; }

    /* DRAWERS */
    .demo-drawers { padding: 12px 16px; display: flex; flex-direction: column; gap: 5px; }
    .demo-drw { cursor: pointer; transition: transform 0.25s cubic-bezier(0.4,0,0.2,1); }
    .demo-drw:hover { transform: translateX(5px); }
    .demo-drw-face { background: linear-gradient(135deg, #3D4F64 0%, #2A3A4A 60%, #243040 100%); border-radius: 7px; border: 1px solid rgba(255,255,255,0.08); border-bottom: 2px solid rgba(0,0,0,0.35); box-shadow: 0 3px 10px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.08); display: flex; align-items: center; overflow: hidden; transition: border-color 0.2s; }
    .demo-drw:hover .demo-drw-face { border-color: rgba(255,255,255,0.18); }
    .demo-drw-strip { width: 4px; align-self: stretch; background: rgba(139,92,246,0.6); flex-shrink: 0; transition: background 0.2s; }
    .demo-drw:hover .demo-drw-strip { background: rgba(139,92,246,1); }
    .demo-drw-handle { width: 32px; height: 32px; border-radius: 7px; margin: 9px 11px; background: linear-gradient(145deg, #8E9EAB, #5D6D7E); border: 1px solid rgba(255,255,255,0.18); box-shadow: 0 2px 6px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.25); display: flex; align-items: center; justify-content: center; font-size: 14px; flex-shrink: 0; }
    .demo-drw-info { flex: 1; padding: 9px 0; min-width: 0; }
    .demo-drw-label { font-size: 13px; font-weight: 700; color: rgba(255,255,255,0.88); }
    .demo-drw-count { font-size: 10px; color: rgba(255,255,255,0.35); margin-top: 2px; }
    .demo-drw-right { display: flex; align-items: center; gap: 8px; padding: 0 14px; flex-shrink: 0; }
    .demo-drw-dot { width: 7px; height: 7px; border-radius: 50%; background: #22C55E; box-shadow: 0 0 6px #22C55E; }
    .demo-drw-arrow { font-size: 12px; color: rgba(255,255,255,0.3); transition: color 0.2s, transform 0.2s; }
    .demo-drw:hover .demo-drw-arrow { color: rgba(255,255,255,0.7); transform: translateX(3px); }

    /* EXPANDED DRAWER CONTENT */
    .demo-drw-content { overflow: hidden; transition: max-height 0.35s ease, opacity 0.3s; opacity: 0; max-height: 0; }
    .demo-drw-content.open { max-height: 400px; opacity: 1; }
    .demo-drw-content-inner { background: rgba(0,0,0,0.25); border-top: 1px solid rgba(255,255,255,0.04); padding: 14px 16px 14px 52px; }
    .demo-drw-item { display: flex; align-items: center; justify-content: space-between; padding: 10px 12px; background: rgba(255,255,255,0.03); border-radius: 8px; margin-bottom: 6px; border: 1px solid rgba(255,255,255,0.05); }
    .demo-drw-item-left { display: flex; flex-direction: column; gap: 2px; }
    .demo-drw-item-name { font-size: 13px; font-weight: 600; color: rgba(255,255,255,0.85); }
    .demo-drw-item-sub { font-size: 11px; color: rgba(255,255,255,0.35); }
    .demo-drw-add-btn { display: flex; align-items: center; gap: 6px; margin-top: 10px; padding: 8px 14px; background: linear-gradient(135deg, #8B5CF6, #38BDF8); border: none; border-radius: 100px; color: #fff; font-family: 'Plus Jakarta Sans', sans-serif; font-size: 12px; font-weight: 700; cursor: pointer; transition: opacity 0.2s; box-shadow: 0 3px 10px rgba(139,92,246,0.35); }
    .demo-drw-add-btn:hover { opacity: 0.85; }

    .demo-int-footer { background: linear-gradient(90deg, #0D1117, #1a1f2e, #0D1117); padding: 10px 24px; display: flex; align-items: center; justify-content: space-between; border-top: 2px solid rgba(0,0,0,0.5); border-radius: 0 0 7px 7px; }
    .demo-int-footer-text { font-size: 9px; font-weight: 700; color: rgba(255,255,255,0.18); letter-spacing: 0.14em; text-transform: uppercase; }
    .demo-cta-hint { font-size: 11px; color: rgba(255,255,255,0.3); font-style: italic; }

    @media(max-width:768px){
      .demo-section { padding: 60px 24px; }
      .demo-door-inner { flex-direction: column; justify-content: center; padding: 60px 32px 90px; gap: 24px; }
      .demo-hinges { display: none; }
      .demo-engraving { text-align: center; align-items: center; }
    }
    .demo-paywall-backdrop { position: fixed; inset: 0; background: rgba(0,0,0,0.75); z-index: 1000; display: flex; align-items: center; justify-content: center; padding: 24px; backdrop-filter: blur(4px); }
    .demo-paywall-modal { background: #fff; border-radius: 24px; padding: 48px 40px; max-width: 460px; width: 100%; text-align: center; box-shadow: 0 40px 80px rgba(0,0,0,0.4); }
    .demo-paywall-icon { font-size: 48px; margin-bottom: 16px; }
    .demo-paywall-title { font-family: 'Fraunces', serif; font-weight: 900; font-size: 28px; color: #1E1040; margin-bottom: 10px; line-height: 1.1; }
    .demo-paywall-title em { font-style: italic; color: #8B5CF6; }
    .demo-paywall-sub { font-size: 15px; color: #6B7280; line-height: 1.6; margin-bottom: 28px; }
    .demo-paywall-price { font-family: 'Fraunces', serif; font-weight: 900; font-size: 42px; color: #1E1040; margin-bottom: 4px; }
    .demo-paywall-price span { font-size: 18px; font-weight: 400; color: #9CA3AF; }
    .demo-paywall-desc { font-size: 13px; color: #9CA3AF; margin-bottom: 24px; }
    .demo-paywall-btn { display: block; width: 100%; padding: 16px; background: linear-gradient(135deg, #8B5CF6, #38BDF8); color: #fff; border: none; border-radius: 100px; font-family: 'Plus Jakarta Sans', sans-serif; font-weight: 700; font-size: 16px; cursor: pointer; box-shadow: 0 8px 24px rgba(139,92,246,0.35); transition: opacity 0.2s, transform 0.2s; margin-bottom: 12px; }
    .demo-paywall-btn:hover { opacity: 0.9; transform: translateY(-2px); }
    .demo-paywall-cancel { background: none; border: none; color: #9CA3AF; font-family: 'Plus Jakarta Sans', sans-serif; font-size: 13px; cursor: pointer; padding: 8px; transition: color 0.2s; }
    .demo-paywall-cancel:hover { color: #6B7280; }
    .demo-paywall-guarantee { font-size: 12px; color: #9CA3AF; margin-top: 16px; }
    .demo-paywall-features { list-style: none; text-align: left; margin: 0 0 24px; display: flex; flex-direction: column; gap: 8px; }
    .demo-paywall-features li { display: flex; align-items: center; gap: 10px; font-size: 14px; color: #374151; }
    .demo-paywall-features li::before { content: '✓'; color: #8B5CF6; font-weight: 800; flex-shrink: 0; }

    /* LETTER MODAL */
    .demo-letter-backdrop { position: fixed; inset: 0; background: rgba(0,0,0,0.6); z-index: 1100; display: flex; align-items: center; justify-content: center; padding: 24px; backdrop-filter: blur(4px); }
    .demo-letter-modal { background: #FFFEF9; border-radius: 20px; padding: 48px; max-width: 540px; width: 100%; box-shadow: 0 40px 80px rgba(0,0,0,0.3); position: relative; }
    .demo-letter-header { display: flex; align-items: center; gap: 12px; margin-bottom: 28px; padding-bottom: 20px; border-bottom: 1px solid #EDE9E3; }
    .demo-letter-icon { font-size: 32px; }
    .demo-letter-to { font-family: 'Fraunces', serif; font-weight: 900; font-size: 22px; color: #1E1040; }
    .demo-letter-from { font-size: 13px; color: #9CA3AF; margin-top: 2px; }
    .demo-letter-body { font-size: 15px; color: #374151; line-height: 1.9; white-space: pre-wrap; font-style: italic; margin-bottom: 28px; }
    .demo-letter-close { position: absolute; top: 20px; right: 20px; background: none; border: none; font-size: 20px; color: #9CA3AF; cursor: pointer; padding: 4px 8px; border-radius: 8px; transition: color 0.2s; }
    .demo-letter-close:hover { color: #374151; }
    .demo-letter-cta { background: linear-gradient(135deg, #8B5CF6, #38BDF8); color: #fff; border: none; border-radius: 100px; padding: 12px 28px; font-family: 'Plus Jakarta Sans', sans-serif; font-weight: 700; font-size: 14px; cursor: pointer; width: 100%; transition: opacity 0.2s; }
    .demo-letter-cta:hover { opacity: 0.88; }
    .demo-letter-note { font-size: 11px; color: #9CA3AF; text-align: center; margin-top: 10px; font-style: italic; }
    .demo-letter-preview-btn { display: flex; align-items: center; gap: 8px; background: rgba(139,92,246,0.08); border: 1px solid rgba(139,92,246,0.2); border-radius: 10px; padding: 10px 14px; cursor: pointer; width: 100%; text-align: left; margin-bottom: 6px; transition: all 0.2s; }
    .demo-letter-preview-btn:hover { background: rgba(139,92,246,0.14); border-color: rgba(139,92,246,0.35); }
    .demo-letter-preview-icon { font-size: 18px; flex-shrink: 0; }
    .demo-letter-preview-text { flex: 1; }
    .demo-letter-preview-name { font-size: 13px; font-weight: 700; color: rgba(255,255,255,0.9); }
    .demo-letter-preview-excerpt { font-size: 11px; color: rgba(255,255,255,0.45); margin-top: 2px; font-style: italic; }
    .demo-letter-preview-read { font-size: 11px; font-weight: 700; color: #A78BFA; flex-shrink: 0; }
  `;

  const rivets = [...Array(12)].map((_,i) => <div key={i} className="demo-rivet" />);

  const renderSectionContent = (key) => {
    switch(key) {
      case 'contacts':
        return DEMO_DATA.contacts.map(c => (
          <div key={c.id} className="demo-drw-item">
            <div className="demo-drw-item-left">
              <div className="demo-drw-item-name">{c.icon} {c.name}</div>
              <div className="demo-drw-item-sub">{c.company} · {c.phone}</div>
            </div>
          </div>
        ));
      case 'accounts':
        return DEMO_DATA.accounts.map(a => (
          <div key={a.id} className="demo-drw-item">
            <div className="demo-drw-item-left">
              <div className="demo-drw-item-name">{a.icon} {a.name}</div>
              <div className="demo-drw-item-sub">Ending ···· {a.last4}</div>
            </div>
            <div style={{fontSize:12,fontWeight:700,color:'#A78BFA'}}>{a.value}</div>
          </div>
        ));
      case 'assets':
        return DEMO_DATA.assets.map(a => (
          <div key={a.id} className="demo-drw-item">
            <div className="demo-drw-item-left">
              <div className="demo-drw-item-name">{a.icon} {a.name}</div>
              <div className="demo-drw-item-sub">{a.location}</div>
            </div>
            <div style={{fontSize:12,fontWeight:700,color:'#A78BFA'}}>{a.value}</div>
          </div>
        ));
      case 'insurance':
        return DEMO_DATA.insurance.map(i => (
          <div key={i.id} className="demo-drw-item">
            <div className="demo-drw-item-left">
              <div className="demo-drw-item-name">{i.icon} {i.name}</div>
              <div className="demo-drw-item-sub">Policy #{i.policy} · Coverage: {i.coverage}</div>
            </div>
          </div>
        ));
      case 'documents':
        return DEMO_DATA.documents.map(d => (
          <div key={d.id} className="demo-drw-item">
            <div className="demo-drw-item-left">
              <div className="demo-drw-item-name">{d.icon} {d.name}</div>
              <div className="demo-drw-item-sub">{d.date}</div>
            </div>
          </div>
        ));
      case 'digital':
        return DEMO_DATA.digital.map(d => (
          <div key={d.id} className="demo-drw-item">
            <div className="demo-drw-item-left">
              <div className="demo-drw-item-name">{d.icon} {d.name}</div>
              <div className="demo-drw-item-sub">{d.username} · {d.action}</div>
            </div>
          </div>
        ));
      case 'wishes':
        return (
          <>
            <div className="demo-drw-item">
              <div className="demo-drw-item-left">
                <div className="demo-drw-item-name">🕊️ Funeral: {DEMO_DATA.wishes.funeral}</div>
                <div className="demo-drw-item-sub">Organ donor · Service preferences noted</div>
              </div>
            </div>
            <button className="demo-letter-preview-btn" onClick={() => setShowLetter(true)}>
              <div className="demo-letter-preview-icon">💌</div>
              <div className="demo-letter-preview-text">
                <div className="demo-letter-preview-name">To My Daughter</div>
                <div className="demo-letter-preview-excerpt">"By the time you read this, I will be gone. But I want you to know..."</div>
              </div>
              <div className="demo-letter-preview-read">Read →</div>
            </button>
            <button className="demo-letter-preview-btn" onClick={handleAddClick}>
              <div className="demo-letter-preview-icon">💌</div>
              <div className="demo-letter-preview-text">
                <div className="demo-letter-preview-name">To My Grandchildren</div>
                <div className="demo-letter-preview-excerpt">Private — unlock vault to read</div>
              </div>
              <div className="demo-letter-preview-read">🔒</div>
            </button>
          </>
        );
      case 'guardians':
        return DEMO_DATA.guardians.map((g, i) => (
          <div key={i} className="demo-drw-item">
            <div className="demo-drw-item-left">
              <div className="demo-drw-item-name">🛡️ {g.name}</div>
              <div className="demo-drw-item-sub">{g.relationship}</div>
            </div>
            <div style={{fontSize:11,fontWeight:700,padding:'3px 8px',borderRadius:100,background:g.status==='accepted'?'rgba(34,197,94,0.15)':'rgba(251,146,60,0.12)',color:g.status==='accepted'?'#4ADE80':'#FB923C'}}>{g.status==='accepted'?'Accepted':'Invited'}</div>
          </div>
        ));
      default: return null;
    }
  };

  return (
    <>
      <style>{style}</style>

      {/* LETTER MODAL */}
      {showLetter && (
        <div className="demo-letter-backdrop" onClick={() => setShowLetter(false)}>
          <div className="demo-letter-modal" onClick={e => e.stopPropagation()}>
            <button className="demo-letter-close" onClick={() => setShowLetter(false)}>✕</button>
            <div className="demo-letter-header">
              <div className="demo-letter-icon">💌</div>
              <div>
                <div className="demo-letter-to">{SAMPLE_LETTER.recipient}</div>
                <div className="demo-letter-from">Written by John Sample · Private until vault is opened</div>
              </div>
            </div>
            <div className="demo-letter-body">{SAMPLE_LETTER.full}</div>
            <button className="demo-letter-cta" onClick={() => { setShowLetter(false); setShowPaywall(true); }}>
              Write your own letter — Create My Vault
            </button>
            <div className="demo-letter-note">Your letters are encrypted and private. Only revealed when your Vault Guardians approve access.</div>
          </div>
        </div>
      )}

      {/* PAYWALL MODAL */}
      {showPaywall && (
        <div className="demo-paywall-backdrop" onClick={() => setShowPaywall(false)}>
          <div className="demo-paywall-modal" onClick={e => e.stopPropagation()}>
            <div className="demo-paywall-icon">🔐</div>
            <h2 className="demo-paywall-title">Ready to create <em>your</em> vault?</h2>
            <p className="demo-paywall-sub">You've seen what a complete Merq Legacy vault looks like. Now create yours — it takes about 30 minutes to organize everything your family needs.</p>
            <div className="demo-paywall-price">$69<span>/year</span></div>
            <div className="demo-paywall-desc">That's $5.75/month — less than a coffee</div>
            <ul className="demo-paywall-features">
              <li>Full vault access — all 8 sections</li>
              <li>Vault Guardian system (3–4 trusted people)</li>
              <li>Unlimited document uploads</li>
              <li>30-day money-back guarantee</li>
            </ul>
            <button className="demo-paywall-btn" onClick={() => navigate('/auth')}>
              Create My Vault — $69/year
            </button>
            <button className="demo-paywall-cancel" onClick={() => setShowPaywall(false)}>
              Keep exploring the demo
            </button>
            <div className="demo-paywall-guarantee">🛡️ 30-day money-back guarantee · No hidden fees</div>
          </div>
        </div>
      )}

      <section className="demo-section">
        <div className="demo-label">Interactive Demo</div>
        <h2 className="demo-title">See inside a <em>real vault</em></h2>
        <p className="demo-sub">This is a sample vault. Unlock it, open the drawers, and see exactly what Merq Legacy looks like when it's complete.</p>
        <div className="demo-badge">👆 Try it — click Unlock below</div>

        <div className="demo-vault-wrap">
          <div className="demo-wall-left" />
          <div className="demo-wall-right" />
          <div className="demo-wall-bottom" />

          <div className="demo-vault">
            <div className="demo-rivets">{rivets}</div>

            <div className="demo-inner-frame">
              {!vaultOpen ? (
                <>
                  <div className="demo-door-closed">
                    <div className="demo-door-inner">
                      <div className="demo-door-inset" />
                      <div className="demo-hinges">
                        <div className="demo-hinge" /><div className="demo-hinge" /><div className="demo-hinge" />
                      </div>
                      <div className="demo-engraving">
                        <div className="demo-engrave-eyebrow">Sample Legacy Vault</div>
                        <div className="demo-engrave-name">John & Jane <em>Sample.</em></div>
                        <div className="demo-engrave-sub">Anytown, USA · Est. 2024<br/>3 Vault Guardians Active</div>
                      </div>
                      <div className="demo-dial-wrap">
                        <div className={`demo-dial${unlocking ? ' fast' : ''}`}>
                          <div className="demo-dial-mark" />
                          <div className="demo-dial-inner" />
                        </div>
                        <div className="demo-dial-label">{unlocking ? 'Unlocking...' : 'Demo Vault'}</div>
                      </div>
                    </div>
                  </div>
                  <button className="demo-unlock-btn" onClick={handleUnlock}>
                    🔓 &nbsp;{unlocking ? 'Unlocking...' : 'Unlock the Demo Vault'}
                  </button>
                </>
              ) : (
                <div className="demo-open-wrap">
                  <div className="demo-door-panel swung">
                    <div className="demo-door-panel-face">
                      <div className="demo-panel-hinges">
                        <div className="demo-panel-hinge" /><div className="demo-panel-hinge" /><div className="demo-panel-hinge" />
                      </div>
                    </div>
                  </div>
                  <div className="demo-interior">
                    <div className="demo-int-bar">
                      <div className="demo-int-lights">
                        <div className="demo-light g" /><div className="demo-light a" />
                        <div className="demo-int-title">John & Jane Sample — Legacy Vault</div>
                      </div>
                      <div className="demo-int-badge">👁️ Demo Mode</div>
                    </div>

                    <div className="demo-drawers">
                      {SECTIONS.map(s => (
                        <div key={s.key} className="demo-drw">
                          <div className="demo-drw-face" onClick={() => handleSectionClick(s.key)}>
                            <div className="demo-drw-strip" />
                            <div className="demo-drw-handle">{s.icon}</div>
                            <div className="demo-drw-info">
                              <div className="demo-drw-label">{s.label}</div>
                              <div className="demo-drw-count">{s.count} item{s.count !== 1 ? 's' : ''}</div>
                            </div>
                            <div className="demo-drw-right">
                              <div className="demo-drw-dot" />
                              <div className="demo-drw-arrow">{activeSection === s.key ? '↓' : '›'}</div>
                            </div>
                          </div>
                          <div className={`demo-drw-content${activeSection === s.key ? ' open' : ''}`}>
                            <div className="demo-drw-content-inner">
                              {renderSectionContent(s.key)}
                              <button className="demo-drw-add-btn" onClick={handleAddClick}>
                                + Add to your own vault
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="demo-int-footer">
                      <div className="demo-int-footer-text">Sample Vault · Demo Mode · No real data</div>
                      <div className="demo-cta-hint">Click any drawer to explore ↑</div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="demo-rivets bottom">{rivets}</div>
          </div>
        </div>
      </section>
    </>
  );
}
